/**
 * Studio store selectors — Phase 4 (menu restructure IA-3 + IA-4).
 *
 * These are pure read-only functions over `StudioState`. They live outside
 * the slice definitions so consumers can import them without creating a
 * circular import with `studio-store.ts`.
 *
 * Usage (per-shell Reader):
 *
 *   const threadId = useStudioStore(getCurrentThreadId)
 *   const shell = useStudioStore(getCurrentShell)
 *
 * Usage (one-shot at call time, e.g. from the chat adapter):
 *
 *   const state = useStudioStore.getState()
 *   const ctx = buildPageContext(state)
 */
import type { StudioState } from './studio-store'

export type StudioShellKind = 'dashboard' | 'commerce' | 'ide'

/**
 * StudioRoute values that belong to the Commerce shell when
 * `view === 'dashboard'`. Kept in sync with `COMMERCE_ROUTES` in
 * `App.tsx` (see IA §2). Any change must land in both places.
 */
const COMMERCE_ROUTES = new Set<string>([
  'commerce',
  'products',
  'orders',
  'revenue',
  'members',
  'billing',
  'analytics',
])

/**
 * Resolve which shell a given state is currently in.
 *
 * - `view === 'workspace'` → IDE (a project is open in the editor).
 * - `view === 'dashboard'` + commerce-family route → Commerce.
 * - Otherwise → Dashboard (home / projects / templates / settings / admin
 *   / library / explore / new-project / design-system / map).
 *
 * Matches the shell-selection branch in `App.tsx`.
 */
export function getCurrentShell(state: StudioState): StudioShellKind {
  if (state.view === 'workspace') return 'ide'
  if (COMMERCE_ROUTES.has(state.studioRoute)) return 'commerce'
  return 'dashboard'
}

/**
 * Derive the active chat thread id per IA §5.4 (hybrid thread model).
 *
 * - IDE shell with a loaded project → `ide-<projectId>` (project-scoped).
 * - Dashboard + Commerce share a single per-account assistant thread
 *   (`account-global`) so users don't lose the conversation when moving
 *   between /studio/* and /studio/commerce/*.
 * - Fallback is `account-global`.
 *
 * The `AssistantRuntimeProvider` is re-mounted via a `key` bound to this
 * value, which effectively resets the in-memory transcript when the user
 * crosses a thread boundary — cheap, and aligned with the Phase 4 plan's
 * "pragmatic fallback #1".
 */
export function getCurrentThreadId(state: StudioState): string {
  if (state.view === 'workspace' && state.currentProjectId) {
    return `ide-${state.currentProjectId}`
  }
  return 'account-global'
}
